import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { AppDispatch } from "@/store"
import {
  fetchOrganizationUsers,
  fetchOrganizationRoles,
  selectOrganizationUsers,
  selectOrganizationRoles,
  selectUsersLoading,
  updateOrganizationUserThunk,
  removeOrganizationUserThunk,
  addOrganizationUserThunk,
} from "../../store/userSlice"
import { selectCurrentOrganizationId } from "@/modules/organizations/store/organizationSlice"
import { DataTable, TableColumn } from "@/modules/shared/ui/components/custom/CustomTable"
import { UserSummary } from "../../dtos/User"
import { format } from "date-fns"
import { AddEditUserDialog } from "../components/AddEditUserDialog"
import { toast } from "sonner"
import { DeleteConfirmationDialog } from "@/modules/recruitment/vacancies/ui/components/DeleteConfirmationDialog"
import { Button } from "@/modules/shared/ui/components/button"
import { FaPlus } from "react-icons/fa"
import { useAuth } from "@/modules/auth/hooks/use-auth"

export default function UsersListPage() {
  const dispatch = useDispatch<AppDispatch>()
  const organizationId = useSelector(selectCurrentOrganizationId)
  const users = useSelector(selectOrganizationUsers)
  const roles = useSelector(selectOrganizationRoles)
  const isLoading = useSelector(selectUsersLoading)
  const { user: currentUser } = useAuth()

  const [isEditOpen, setIsEditOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState<UserSummary | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [userToDelete, setUserToDelete] = useState<UserSummary | null>(null)

  useEffect(() => {
    if (organizationId) {
      dispatch(fetchOrganizationUsers(organizationId))
      dispatch(fetchOrganizationRoles(organizationId))
    }
  }, [dispatch, organizationId])

  const handleEditClick = (user: UserSummary) => {
    setSelectedUser(user)
    setIsEditOpen(true)
  }

  const handleAddClick = () => {
    setSelectedUser(null)
    setIsEditOpen(true)
  }

  const handleUpdateUser = async (data: {
    email: string
    firstName: string
    lastName: string
    phoneNumber?: string
    roleId?: string
  }) => {
    if (!organizationId) return

    setIsSubmitting(true)
    try {
      if (selectedUser) {
        // Update profile fields and role
        await dispatch(
          updateOrganizationUserThunk({
            organizationId,
            userId: selectedUser.id,
            data: { 
              firstName: data.firstName, 
              lastName: data.lastName, 
              phoneNumber: data.phoneNumber,
              roleId: data.roleId 
            },
          })
        ).unwrap()

        toast.success("User updated successfully")
      } else {
        // Add mode
        await dispatch(
          addOrganizationUserThunk({
            organizationId,
            data: { 
              email: data.email, 
              firstName: data.firstName, 
              lastName: data.lastName, 
              phoneNumber: data.phoneNumber,
              roleId: data.roleId
            },
          })
        ).unwrap()

        toast.success("User added successfully")
      }
      setIsEditOpen(false)
    } catch (error: unknown) {
      toast.error(typeof error === "string" ? error : "Failed to process request")
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeleteClick = (user: UserSummary) => {
    setUserToDelete(user)
    setIsDeleteOpen(true)
  }

  const handleRemoveUser = async () => {
    if (!organizationId || !userToDelete) return

    setIsSubmitting(true)
    try {
      await dispatch(
        removeOrganizationUserThunk({
          organizationId,
          userId: userToDelete.id,
        })
      ).unwrap()

      toast.success("User removed from organization")
      setIsDeleteOpen(false)
    } catch (error: unknown) {
      toast.error(typeof error === "string" ? error : "Failed to remove user")
    } finally {
      setIsSubmitting(false)
    }
  }

  const columns: TableColumn<UserSummary>[] = [
    {
      key: "firstName",
      label: "Name",
      render: (user) => (
        <div className="flex flex-col">
          <span className="font-medium text-foreground">
            {user.firstName} {user.lastName}
          </span>
        </div>
      ),
    },
    {
      key: "email",
      label: "Email",
      headerClassName: "text-center",
      render: (user) => (
        <div className="text-center">
          <span className="text-muted-foreground">{user.email}</span>
        </div>
      ),
    },
    {
      key: "phoneNumber",
      label: "Phone",
      headerClassName: "text-center",
      render: (user) => (
        <div className="text-center">
          <span className="text-muted-foreground">{user.phoneNumber || "—"}</span>
        </div>
      ),
    },
    {
      key: "roleName",
      label: "Role",
      headerClassName: "text-center",
      render: (user) => (
        <div className="text-center">
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${user.roleName === "OrganizationOwner"
                ? "bg-purple-100 text-purple-800"
                : user.roleName === "TeamMember"
                  ? "bg-primary/10 text-primary"
                  : user.roleName === "Employee"
                    ? "bg-teal-100 text-teal-800"
                    : "bg-muted text-muted-foreground"
              }`}
          >
            {user.roleName ?? "—"}
          </span>
        </div>
      ),
    },
    {
      key: "status",
      label: "Status",
      render: (user) => (
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${user.status === "Accepted"
              ? "bg-green-100 text-green-800 border border-green-200"
              : user.status === "Pending"
                ? "bg-amber-100 text-amber-800 border border-amber-200"
                : user.status === "Declined"
                  ? "bg-destructive/10 text-destructive border border-destructive/20"
                  : "bg-muted text-foreground border border-border"
            }`}
        >
          {user.status}
        </span>
      ),
    },
    {
      key: "createdAt",
      label: "Joined Date",
      render: (user) => (
        <span className="text-muted-foreground">
          {user.createdAt ? format(new Date(user.createdAt), "MMM d, yyyy") : "N/A"}
        </span>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground tracking-tight">User Management</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage your organization's members and their access levels.
          </p>
        </div>
        <Button onClick={handleAddClick} className="bg-primary hover:bg-primary/90 text-primary-foreground">
          <FaPlus className="text-xs" />
          Add User
        </Button>
      </div>

      <div className="bg-card rounded-xl shadow-sm border border-border overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <DataTable<UserSummary>
            columns={columns}
            data={users}
            emptyMessage="No users found in this organization."
            actions={(user) => [
              {
                label: "Edit",
                onClick: () => handleEditClick(user),
              },
              {
                label: "Remove from organization",
                onClick: () => handleDeleteClick(user),
                variant: "destructive",
              },
            ]}
          />
        )}
      </div>

      <AddEditUserDialog
        open={isEditOpen}
        onOpenChange={setIsEditOpen}
        user={selectedUser}
        isSubmitting={isSubmitting}
        roles={roles}
        onSubmit={handleUpdateUser}
      />

      <DeleteConfirmationDialog
        open={isDeleteOpen}
        onOpenChange={setIsDeleteOpen}
        onConfirm={handleRemoveUser}
        isDeleting={isSubmitting}
        title="Remove User"
        description={`Are you sure you want to remove ${userToDelete?.firstName} ${userToDelete?.lastName} from this organization? This action cannot be undone.`}
        confirmDisabled={currentUser?.user?.id === userToDelete?.id}
        disabledMessage="You cannot remove your own account from the organization."
        deleteButtonText="Remove"
        deleteButtonProcessingText="Removing..."
      />
    </div>
  )
}
